import {
  DropdownWrapper,
  messageWithOrWithoutCount,
  SelectionProviderChildProps,
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { defaultMemoize } from 'reselect';
import { Messages } from '../../../gen-src/Localize';
import { WorkbookQuery_workbooks_embeddedDatasources } from '../../../gen-src/queries/WorkbookQuery';
import { WorkbookCustomSqlQueryWrapper } from '../CustomSql/WorkbookCustomSqlQueryWrapper';
import { LineageItemType } from '../lineage/LineageItemType';
import { LineageItemConfig, LineageSection, LineageSectionType } from '../placePage/LineageConfiguration';
import { LineageSubplaceDropdownLabel } from '../placePage/LineageSubplaceDropdownLabel';
import { PaneWithTitle } from '../placePage/PaneWithTitle';
import { DashboardsGridSortProvider } from '../sorting/sortProviders/DashboardsGridSortProvider';
import { DatabasesGridSortProvider } from '../sorting/sortProviders/DatabasesGridSortProvider';
import { DatasourcesGridSortProvider } from '../sorting/sortProviders/DatasourcesGridSortProvider';
import { FieldsGridSortProvider } from '../sorting/sortProviders/FieldsGridSortProvider';
import { FlowsGridSortProvider } from '../sorting/sortProviders/FlowsGridSortProvider';
import { MetricsGridSortProvider } from '../sorting/sortProviders/MetricsGridSortProvider';
import { SheetsGridSortProvider } from '../sorting/sortProviders/SheetsGridSortProvider';
import { TablesGridSortProvider } from '../sorting/sortProviders/TablesGridSortProvider';
import { VirtualConnectionTablesGridSortProvider } from '../sorting/sortProviders/VirtualConnectionTablesGridSortProvider';
import { VirtualConnectionsGridSortProvider } from '../sorting/sortProviders/VirtualConnectionsGridSortProvider';
import { WorkbookDownstreamDashboardsQueryGrid } from './WorkbookDownstreamDashboardsQueryGrid';
import { WorkbookDownstreamMetricsQueryGrid } from './WorkbookDownstreamMetricsQueryGrid';
import { WorkbookDownstreamOwnersPane } from './WorkbookDownstreamOwnersPane';
import { WorkbookDownstreamSheetsQueryGrid } from './WorkbookDownstreamSheetsQueryGrid';
import { WorkbookEmbeddedDatasourceSelector } from './WorkbookEmbeddedDatasourceSelector';
import { WorkbookFieldsQueryGrid } from './WorkbookFieldsQueryGrid';
import { WorkbookLineageCounts } from './WorkbookLineageCountsQueryWrapper';
import { WorkbookUpstreamDatabasesQueryGrid } from './WorkbookUpstreamDatabasesQueryGrid';
import { WorkbookUpstreamDatasourcesQueryGrid } from './WorkbookUpstreamDatasourcesQueryGrid';
import { WorkbookUpstreamFlowsQueryGrid } from './WorkbookUpstreamFlowsQueryGrid';
import { WorkbookUpstreamTablesQueryGrid } from './WorkbookUpstreamTablesQueryGrid';
import { WorkbookUpstreamVirtualConnectionTablesQueryGrid } from './WorkbookUpstreamVirtualConnectionTablesQueryGrid';
import { WorkbookUpstreamVirtualConnectionsQueryGrid } from './WorkbookUpstreamVirtualConnectionsQueryGrid';

export type WorkbookLineageItemConfig = LineageItemConfig<WorkbookLineageCounts, WorkbookLineagePaneParams>;
export type WorkbookLineageSection = LineageSection<WorkbookLineageCounts, WorkbookLineagePaneParams>;

export type WorkbookLineagePaneParams = SelectionProviderChildProps & {
  readonly title: string;
  readonly vizportalUrlId: string;
  readonly filteredFieldId: string | null;
  readonly selectedEmbeddedDatasourceId: string;
  readonly fieldsCountForSelectedEmbeddedDatasource: number | null;
  readonly count: number | null;
  readonly updateEmbeddedDatasourceSelection: (embeddedDatasourceId: string) => void;
  readonly embeddedDatasources: ReadonlyArray<WorkbookQuery_workbooks_embeddedDatasources>;
};

const workbookUpstreamVirtualConnection: WorkbookLineageItemConfig = {
  lineageItemType: LineageItemType.VirtualConnection,
  messagesForLineage: { withCount: Messages.VirtualConnectionsWithCount, withoutCount: Messages.VirtualConnections },
  messagesForPane: { withCount: Messages.UpstreamVirtualConnectionsWithCount, withoutCount: Messages.UpstreamVirtualConnections },
  count: counts => counts.upstreamVirtualConnections,
  path: (urls, vizportalUrlId) => urls.workbookLineageUpstreamVirtualConnectionsPath(vizportalUrlId),
  paneFactory: (paneParams) => (
    <PaneWithTitle title={paneParams.title}>
      <VirtualConnectionsGridSortProvider>
        {({ orderBy, updateSortOrder }) =>
          <WorkbookUpstreamVirtualConnectionsQueryGrid
            vizportalUrlId={paneParams.vizportalUrlId}
            filteredFieldId={paneParams.filteredFieldId}
            rowCount={paneParams.count}
            orderBy={orderBy}
            updateSortOrder={updateSortOrder}
          />
        }
      </VirtualConnectionsGridSortProvider>
    </PaneWithTitle>
  ),
};

const workbookUpstreamVirtualConnectionTable: WorkbookLineageItemConfig = {
  lineageItemType: LineageItemType.VirtualConnectionTable,
  messagesForLineage: { withCount: Messages.VirtualConnectionTablesWithCount, withoutCount: Messages.VirtualConnectionTables },
  messagesForPane: { withCount: Messages.UpstreamVirtualConnectionTablesWithCount, withoutCount: Messages.UpstreamVirtualConnectionTables },
  count: counts => counts.upstreamVirtualConnectionTables,
  path: (urls, vizportalUrlId) => urls.workbookLineageUpstreamVirtualConnectionTablesPath(vizportalUrlId),
  paneFactory: (paneParams) => (
    <PaneWithTitle title={paneParams.title}>
      <VirtualConnectionTablesGridSortProvider>
        {({ orderBy, updateSortOrder }) =>
          <WorkbookUpstreamVirtualConnectionTablesQueryGrid
            vizportalUrlId={paneParams.vizportalUrlId}
            filteredFieldId={paneParams.filteredFieldId}
            rowCount={paneParams.count}
            orderBy={orderBy}
            updateSortOrder={updateSortOrder}
          />
        }
      </VirtualConnectionTablesGridSortProvider>
    </PaneWithTitle>
  ),
};

function getWorkbookUpstreamItems(publishedConnectionsLineageEnabled: boolean): ReadonlyArray<WorkbookLineageItemConfig> {
  return [
    {
      lineageItemType: LineageItemType.Database,
      messagesForLineage: { withCount: Messages.DatabasesWithCount, withoutCount: Messages.Databases },
      messagesForPane: { withCount: Messages.UpstreamDatabasesWithCount, withoutCount: Messages.UpstreamDatabases },
      count: counts => counts.upstreamDatabases,
      path: (urls, vizportalUrlId) => urls.workbookLineageUpstreamDatabasesPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DatabasesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <WorkbookUpstreamDatabasesQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredFieldId={paneParams.filteredFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </DatabasesGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Table,
      messagesForLineage: { withCount: Messages.TablesWithCount, withoutCount: Messages.Tables },
      messagesForPane: { withCount: Messages.UpstreamTablesWithCount, withoutCount: Messages.UpstreamTables },
      count: counts => counts.upstreamTables,
      path: (urls, vizportalUrlId) => urls.workbookLineageUpstreamTablesPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <TablesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <WorkbookUpstreamTablesQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredFieldId={paneParams.filteredFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </TablesGridSortProvider>
        </PaneWithTitle>
      ),
    },
    ...(publishedConnectionsLineageEnabled ? [workbookUpstreamVirtualConnection, workbookUpstreamVirtualConnectionTable] : []),
    {
      lineageItemType: LineageItemType.Flow,
      messagesForLineage: { withCount: Messages.FlowsWithCount, withoutCount: Messages.Flows },
      messagesForPane: { withCount: Messages.UpstreamFlowsWithCount, withoutCount: Messages.UpstreamFlows },
      count: counts => counts.upstreamFlows,
      path: (urls, vizportalUrlId) => urls.workbookLineageUpstreamFlowsPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <FlowsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <WorkbookUpstreamFlowsQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredFieldId={paneParams.filteredFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </FlowsGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Datasource,
      messagesForLineage: { withCount: Messages.DatasourcesWithCount, withoutCount: Messages.Datasources },
      messagesForPane: { withCount: Messages.UpstreamDatasourcesWithCount, withoutCount: Messages.UpstreamDatasources },
      count: counts => counts.upstreamDatasources,
      path: (urls, vizportalUrlId) => urls.workbookLineageUpstreamDatasourcesPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DatasourcesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <WorkbookUpstreamDatasourcesQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredFieldId={paneParams.filteredFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </DatasourcesGridSortProvider>
        </PaneWithTitle>
      ),
    },
  ];
}
const memoizedGetWorkbookUpstreamItems = defaultMemoize(getWorkbookUpstreamItems);

function WorkbookAnchor(incompleteCustomSqlEnabled: boolean): WorkbookLineageItemConfig {
  return {
    lineageItemType: LineageItemType.Workbook,
    messagesForLineage: { withCount: Messages.EmbeddedDatasourcesWithCount, withoutCount: Messages.EmbeddedDatasources },
    messagesForPane: { withCount: Messages.FieldsWithCount, withoutCount: Messages.Fields },
    count: _ => NaN, // The anchor gets its own count from the LineageNav props, and the two pane counts are passed in via paneParams.
    path: (urls, vizportalUrlId) => urls.workbookLineageFieldsPath(vizportalUrlId),
    paneFactory: (paneParams) => (
      <>
        <div>
          <LineageSubplaceDropdownLabel
            label={messageWithOrWithoutCount(paneParams.embeddedDatasources.length, {
              withCount: Messages.EmbeddedDatasourcesWithCount,
              withoutCount: Messages.EmbeddedDatasources
            })}
          >
            <DropdownWrapper>
              <WorkbookEmbeddedDatasourceSelector
                embeddedDatasources={paneParams.embeddedDatasources}
                updateEmbeddedDatasourceSelection={paneParams.updateEmbeddedDatasourceSelection}
                vizportalUrlId={paneParams.vizportalUrlId}
                selectedEmbeddedDatasource={paneParams.selectedEmbeddedDatasourceId}
              />
            </DropdownWrapper>
          </LineageSubplaceDropdownLabel>
        </div>
        {
          incompleteCustomSqlEnabled ?
            <WorkbookCustomSqlQueryWrapper
              workbookId={paneParams.vizportalUrlId}
              embeddedDatasourceId={paneParams.selectedEmbeddedDatasourceId}
            />
            : null
        }
        <PaneWithTitle
          title={messageWithOrWithoutCount(
            paneParams.fieldsCountForSelectedEmbeddedDatasource,
            { withCount: Messages.FieldsWithCount, withoutCount: Messages.Fields },
          )}
        >
          <FieldsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <WorkbookFieldsQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                selectedKeys={paneParams.selectedKeys}
                selectedEmbeddedDatasourceId={paneParams.selectedEmbeddedDatasourceId}
                updateSelectionForKey={paneParams.updateSelectionForKey}
                rowCount={paneParams.fieldsCountForSelectedEmbeddedDatasource}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </FieldsGridSortProvider>
        </PaneWithTitle>
      </>
    ),
  };
}

const workbookDownstreamMetrics: WorkbookLineageItemConfig = {
  lineageItemType: LineageItemType.Metric,
  messagesForLineage: { withCount: Messages.MetricsWithCount, withoutCount: Messages.Metrics },
  messagesForPane: { withCount: Messages.DownstreamMetricsWithCount, withoutCount: Messages.DownstreamMetrics },
  count: counts => counts.downstreamMetrics,
  path: (urls, vizportalUrlId) => urls.workbookLineageDownstreamMetricsPath(vizportalUrlId),
  paneFactory: (paneParams) => (
    <PaneWithTitle title={paneParams.title}>
      <MetricsGridSortProvider>
        {({ orderBy, updateSortOrder }) =>
          <WorkbookDownstreamMetricsQueryGrid
            vizportalUrlId={paneParams.vizportalUrlId}
            filteredFieldId={paneParams.filteredFieldId}
            rowCount={paneParams.count}
            orderBy={orderBy}
            updateSortOrder={updateSortOrder}
          />}
      </MetricsGridSortProvider>
    </PaneWithTitle>
  ),
};

function getWorkbookDownstreamItems(metricsDataCatalogEnabled: boolean): ReadonlyArray<WorkbookLineageItemConfig> {
  return [
    {
      lineageItemType: LineageItemType.Sheet,
      messagesForLineage: { withCount: Messages.SheetsWithCount, withoutCount: Messages.Sheets },
      messagesForPane: { withCount: Messages.DownstreamSheetsWithCount, withoutCount: Messages.DownstreamSheets },
      count: counts => counts.downstreamSheets,
      path: (urls, vizportalUrlId) => urls.workbookLineageDownstreamSheetsPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <SheetsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <WorkbookDownstreamSheetsQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredFieldId={paneParams.filteredFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </SheetsGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Dashboard,
      messagesForLineage: { withCount: Messages.DashboardsWithCount, withoutCount: Messages.Dashboards },
      messagesForPane: { withCount: Messages.DownstreamDashboardsWithCount, withoutCount: Messages.DownstreamDashboards },
      count: counts => counts.downstreamDashboards,
      path: (urls, vizportalUrlId) => urls.workbookLineageDownstreamDashboardsPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DashboardsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <WorkbookDownstreamDashboardsQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredFieldId={paneParams.filteredFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </DashboardsGridSortProvider>
        </PaneWithTitle>
      ),
    },
    ...(metricsDataCatalogEnabled ? [workbookDownstreamMetrics] : []),
  ];
}
const memoizedGetWorkbookDownstreamItems = defaultMemoize(getWorkbookDownstreamItems);

const WorkbookOwners: WorkbookLineageItemConfig = {
  lineageItemType: LineageItemType.User,
  messagesForLineage: { withCount: Messages.OwnersWithCount, withoutCount: Messages.Owners },
  messagesForPane: { withCount: Messages.DownstreamOwnersWithCount, withoutCount: Messages.DownstreamOwners },
  count: counts => counts.downstreamOwners,
  path: (urls, vizportalUrlId) => urls.workbookLineageDownstreamOwnersPath(vizportalUrlId),
  paneFactory: (paneParams) => (
    <WorkbookDownstreamOwnersPane
      title={paneParams.title}
      vizportalUrlId={paneParams.vizportalUrlId}
      filteredFieldId={paneParams.filteredFieldId}
      rowCount={paneParams.count}
    />
  ),
};

function getWorkbookLineageConfiguration(metricsDataCatalogEnabled: boolean, incompleteCustomSqlEnabled: boolean, publishedConnectionsLineageEnabled: boolean): ReadonlyArray<WorkbookLineageSection> {
  return [
    {
      sectionType: LineageSectionType.Group,
      key: 'upstream',
      items: memoizedGetWorkbookUpstreamItems(publishedConnectionsLineageEnabled),
    },
    {
      sectionType: LineageSectionType.Anchor,
      key: 'anchor',
      items: [WorkbookAnchor(incompleteCustomSqlEnabled)],
    },
    {
      sectionType: LineageSectionType.Group,
      key: 'downstream',
      items: memoizedGetWorkbookDownstreamItems(metricsDataCatalogEnabled),
    },
    {
      sectionType: LineageSectionType.Owners,
      key: 'owners',
      items: [WorkbookOwners],
    },
  ];
}
export const memoizedGetWorkbookLineageConfiguration = defaultMemoize(getWorkbookLineageConfiguration);
